import { apiSlice } from './apiSlice';
import { NotificationActions } from './notificationSlice';

export const contractSlice = apiSlice.injectEndpoints({
    endpoints: builder => ({
        getContracts: builder.query({
            query: ({ organizationId }) => `/organizations/${organizationId}/contracts`,
            providesTags: (result = [], error, arg) => [
                'Contract',
                ...(result ?? []).map(({ id }) => ({ type: 'Contract', id }))
            ],
        }),
        getContract: builder.query({
            query: (contractId) => `/contracts/${contractId}`,
            providesTags: (result, error, arg) => [{ type: 'Contract', id: arg }],
        }),
        createContract: builder.mutation({
            query: ({ name, organizationId }) => ({
                url: `/organizations/${organizationId}/contracts`,
                method: 'POST',
                body: { name }
            }),
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                } catch {
                    dispatch(NotificationActions.addErrorNotification({
                        message: 'Failed to create contract. Please try again.'
                    }));
                }
            }
        }),
        updateContract: builder.mutation({
            query: ({ contractId, data }) => ({
                url: `/contracts/${contractId}`,
                method: 'PATCH',
                body: data
            }),
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                } catch {
                    dispatch(NotificationActions.addErrorNotification({
                        message: 'Failed to save contract changes. Please try again.'
                    }));
                }
            }
        }),
        replaceContractHospitals: builder.mutation({
            query: ({ contractId, hospitalIds }) => ({
                url: `/contracts/${contractId}/hospitals`,
                method: 'PUT',
                body: { hospital_ids: hospitalIds }
            }),
            invalidatesTags: (result, error, arg) => [{ type: 'Contract', id: arg.contractId }],
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(NotificationActions.addSuccessNotification({
                        message: 'Contract hospitals updated successfully.'
                    }));
                } catch {
                    dispatch(NotificationActions.addErrorNotification({
                        message: 'Failed to update contract hospitals. Please try again.'
                    }));
                }
            }
        }),
        createContractExtraction: builder.mutation({
            query: ({ contractId, file }) => {
                const formData = new FormData();
                formData.append('file', file);
                return {
                    url: `/contracts/${contractId}/extractions`,
                    method: 'POST',
                    body: formData,
                };
            },
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                } catch {
                    dispatch(NotificationActions.addErrorNotification({
                        message: 'Failed to create contract extraction. Please try again.'
                    }));
                }
            }
        }),
        getContractExtraction: builder.query({
            query: (extractionId) => `/contractExtractions/${extractionId}`,
        }),
        createRebates: builder.mutation({
            query: ({ contractId, rebates }) => ({
                url: `/contracts/${contractId}/rebates`,
                method: 'POST',
                body: { rebates }
            }),
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                } catch {
                    dispatch(NotificationActions.addErrorNotification({
                        message: 'Failed to create rebates. Please try again.',
                    }));
                }
            },
        }),
        updateRebate: builder.mutation({
            query: ({ rebate }) => ({
                url: `/rebates/${rebate}`,
                method: 'PATCH',
                body: { rebate }
            }),
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                } catch {
                    dispatch(NotificationActions.addErrorNotification({
                        message: 'Failed to save changes to rebate. Please try again.'
                    }));
                }
            }
        }),

    }),
});

export const {
    useGetContractsQuery,
    useGetContractQuery,
    useCreateContractMutation,
    useUpdateContractMutation,
    useReplaceContractHospitalsMutation,
    useCreateContractExtractionMutation,
    useLazyGetContractExtractionQuery,
    useCreateRebatesMutation,
    useUpdateRebateMutation
} = contractSlice;
